import React, { useState, useRef } from 'react';
import { Search, Loader2, User, X } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { salesOrderService } from '../services/salesOrderService';
import { Customer } from '../types/salesOrder';

const formatDocument = (doc?: string) => {
  if (!doc) return '';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

interface CustomerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

export const CustomerSearchModal: React.FC<CustomerSearchModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = (value: string) => {
    setQuery(value);
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await salesOrderService.searchCustomers(value.trim());
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 350);
  };

  const handleSelect = (customer: Customer) => {
    onSelect(customer);
    resetAndClose();
  };

  const resetAndClose = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={resetAndClose} title="Buscar Cliente" size="lg">
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="w-full input-field pl-9 pr-8 py-2.5 text-sm"
            placeholder="Pesquisar por nome, CPF/CNPJ, e-mail ou telefone..."
            value={query}
            onChange={e => handleSearch(e.target.value)}
            autoFocus
          />
          {query && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => { setQuery(''); setResults([]); setSearched(false); }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Buscando clientes...
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {results.map(customer => (
                <button
                  key={customer.customer_id}
                  type="button"
                  className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                  onClick={() => handleSelect(customer)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 p-1.5 bg-slate-100 rounded-full">
                      <User size={14} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{customer.name || '—'}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                        {customer.document && (
                          <span className="text-xs text-slate-500">{formatDocument(customer.document)}</span>
                        )}
                        {customer.email && (
                          <span className="text-xs text-slate-500">{customer.email}</span>
                        )}
                        {customer.cell_phone && (
                          <span className="text-xs text-slate-500">{customer.cell_phone}</span>
                        )}
                        {customer.phone && !customer.cell_phone && (
                          <span className="text-xs text-slate-500">{customer.phone}</span>
                        )}
                      </div>
                      {customer.default_address && (
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {customer.default_address.addr_street}
                          {customer.default_address.addr_num ? `, ${customer.default_address.addr_num}` : ''}
                          {customer.default_address.addr_city ? ` — ${customer.default_address.addr_city}` : ''}
                          {customer.default_address.addr_state ? `/${customer.default_address.addr_state}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : searched ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Nenhum cliente encontrado para "{query}"
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 text-sm">
              Digite para buscar clientes por nome, CPF/CNPJ, e-mail ou telefone
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
        <Button variant="outline" onClick={resetAndClose}>Fechar</Button>
      </div>
    </Dialog>
  );
};
